import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, CalendarDays, CheckCircle2, XCircle } from "lucide-react";
import formatApiError from "../lib/apiError";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const REASON_LABEL = {
  content_not_available: "Content Not Available",
  technical_issues:      "Technical Issues",
};

function buildMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const startWeekday = firstDay.getDay();
  const start = new Date(year, month, 1 - startWeekday);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, "0");
    const dd   = String(d.getDate()).padStart(2, "0");
    cells.push({ date: d, inMonth: d.getMonth() === month, key: `${yyyy}-${mm}-${dd}` });
  }
  return cells;
}

const isSameDate = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth()    === b.getMonth() &&
  a.getDate()     === b.getDate();

export default function AdminTimetablePage() {
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/timetable`, ax());
      setEntries(data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load timetable."));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Group by date → { used, notUsed, total, items }
  const countsByDate = useMemo(() => {
    const map = {};
    for (const e of entries) {
      if (!map[e.date]) map[e.date] = { used: 0, notUsed: 0, total: 0, items: [] };
      if (e.medimagic_used) map[e.date].used++;
      else map[e.date].notUsed++;
      map[e.date].total++;
      map[e.date].items.push(e);
    }
    return map;
  }, [entries]);

  const grid = useMemo(() => buildMonthGrid(view.year, view.month), [view]);

  const goPrev = () => setView((v) => {
    const m = v.month - 1;
    return m < 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: m };
  });
  const goNext = () => setView((v) => {
    const m = v.month + 1;
    return m > 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: m };
  });
  const goToday = () => setView({ year: today.getFullYear(), month: today.getMonth() });

  // Monthly stats
  const monthStats = useMemo(() => {
    const inMonth = entries.filter((e) => {
      const d = new Date(e.date);
      return d.getFullYear() === view.year && d.getMonth() === view.month;
    });
    const used = inMonth.filter((e) => e.medimagic_used).length;
    return { total: inMonth.length, used, notUsed: inMonth.length - used };
  }, [entries, view]);

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Timetable</h1>
          <p className="page-sub">Daily totals across all colleges — green = Medimagic used, red = not used.</p>
        </div>
        <button className="btn-ghost" onClick={goToday} data-testid="cal-today-btn">
          <CalendarDays size={15} /> Today
        </button>
      </div>

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="cal-card">
          <div className="cal-header">
            <button className="cal-nav-btn" onClick={goPrev} data-testid="cal-prev-btn" aria-label="Previous month">
              <ChevronLeft size={18} />
            </button>
            <h2 className="cal-month-title" data-testid="cal-month-title">
              {MONTHS[view.month]} {view.year}
            </h2>
            <button className="cal-nav-btn" onClick={goNext} data-testid="cal-next-btn" aria-label="Next month">
              <ChevronRight size={18} />
            </button>

            <div className="cal-stats">
              <span className="cal-stat-pill"><strong>{monthStats.total}</strong> Total</span>
              <span className="cal-stat-pill cal-stat-used">
                <CheckCircle2 size={12} /> <strong>{monthStats.used}</strong> Used
              </span>
              <span className="cal-stat-pill cal-stat-not-used">
                <XCircle size={12} /> <strong>{monthStats.notUsed}</strong> Not Used
              </span>
            </div>
          </div>

          <div className="cal-weekdays">
            {WEEKDAYS.map((d) => <div key={d} className="cal-weekday">{d}</div>)}
          </div>

          <div className="cal-grid">
            {grid.map((cell) => {
              const c = countsByDate[cell.key];
              const isToday = isSameDate(cell.date, today);
              return (
                <div
                  key={cell.key}
                  className={`cal-cell${cell.inMonth ? "" : " cal-cell-outside"}${isToday ? " cal-cell-today" : ""}${c ? " cal-cell-has-events" : ""}`}
                  data-testid={`cal-cell-${cell.key}`}
                >
                  <div className="cal-cell-head">
                    <span className="cal-date-num">{cell.date.getDate()}</span>
                    {c && <span className="cal-count">{c.total}</span>}
                  </div>
                  {c && (
                    <div className="cal-counts">
                      {c.used > 0 && (
                        <span className="cal-count-pill cal-count-pill-used">
                          <CheckCircle2 size={11} />
                          <strong>{c.used}</strong> Used
                        </span>
                      )}
                      {c.notUsed > 0 && (
                        <span className="cal-count-pill cal-count-pill-not-used">
                          <XCircle size={11} />
                          <strong>{c.notUsed}</strong> Not Used
                        </span>
                      )}
                    </div>
                  )}

                  {c && (
                    <div className="cal-popover" role="tooltip" data-testid={`cal-popover-${cell.key}`}>
                      <p className="cal-popover-title">
                        {cell.date.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
                      </p>
                      <p className="cal-popover-sub">
                        {c.total} {c.total === 1 ? "class" : "classes"} · {c.used} Used · {c.notUsed} Not Used
                      </p>
                      <div className="cal-popover-list">
                        {c.items.slice(0, 8).map((e) => (
                          <div key={e.id} className={`cal-popover-row ${e.medimagic_used ? "is-used" : "is-not-used"}`}>
                            <span className="cal-popover-dot" />
                            <div className="cal-popover-row-body">
                              <p className="cal-popover-row-title">
                                {e.subject_code && <span className="cal-popover-code">{e.subject_code}</span>}
                                {e.subject_name || "—"}
                              </p>
                              <p className="cal-popover-row-meta">
                                {e.professor_name || "—"}
                                {e.college_name && <> · {e.college_name}</>}
                              </p>
                              {!e.medimagic_used && (e.cancellation_reason || e.cancellation_description) && (
                                <p className="cal-popover-row-reason">
                                  {REASON_LABEL[e.cancellation_reason] || e.cancellation_reason}
                                  {e.cancellation_description && <> — {e.cancellation_description}</>}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                        {c.items.length > 8 && (
                          <p className="cal-popover-more">+ {c.items.length - 8} more…</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="cal-legend">
            <span className="cal-legend-item">
              <span className="cal-legend-swatch cal-legend-used" />
              Medimagic Used
            </span>
            <span className="cal-legend-item">
              <span className="cal-legend-swatch cal-legend-not-used" />
              Not Used
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
